import React from "react";
import { useTranslation } from "react-i18next";
import { IconButton, Select, TextField, Tooltip, toast } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { Copy01Icon } from "@hugeicons-pro/core-stroke-rounded";
import PlatformIntegrationFieldType from "enums/PlatformIntegrationFieldType";
import { PlatformIntegrationField } from "hooks/usePlatformIntegrations";
import { Stack } from "components/layout/reusableLayouts";

const STORED_SECRET_MASK = "••••••••";

interface IntegrationFieldInputProps {
  field: PlatformIntegrationField;
  value: string;
  /** A secret that holds a value; it is never sent back, so the field stays empty. */
  stored: boolean;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: string;
}

export const EndpointUrlField: React.FC<{ url: string }> = ({ url }) => {
  const { t } = useTranslation();

  const copy = async () => {
    await navigator.clipboard.writeText(url);
    toast.success(t("WebhookUrlCopiedToast"));
  };

  return (
    <TextField
      label={t("Endpoint URL")}
      value={url}
      readOnly
      fullWidth
      trailing={
        <Tooltip describeChild title={t("Copy webhook URL")}>
          <IconButton
            size="small"
            variant="ghost"
            aria-label={t("Copy {{field}}", { field: url })}
            onClick={() => void copy()}
          >
            <HugeiconsIcon icon={Copy01Icon} size={16} />
          </IconButton>
        </Tooltip>
      }
    />
  );
};

/** One field of a provider's template, drawn the way its type says. */
const IntegrationFieldInput: React.FC<IntegrationFieldInputProps> = ({
  field,
  value,
  stored,
  onChange,
  disabled,
  error,
}) => {
  const { t } = useTranslation();
  const secret = field.type === PlatformIntegrationFieldType.SECRET;
  const helper =
    secret && stored
      ? t("SecretNotShown")
      : field.helpKey
        ? t(field.helpKey)
        : undefined;

  if (field.type === PlatformIntegrationFieldType.SELECT) {
    return (
      <Select
        label={t(field.labelKey)}
        size="md"
        required={field.required}
        clearable={false}
        value={value || null}
        onChange={(id) => onChange(id ?? "")}
        options={(field.options ?? []).map((option) => ({
          id: option.value,
          label: option.label,
        }))}
        helperText={helper}
        errorText={error}
        disabled={disabled}
        fullWidth
      />
    );
  }

  return (
    <Stack gap="var(--space-3)">
      {field.endpointUrl && <EndpointUrlField url={field.endpointUrl} />}
      <TextField
        label={t(field.labelKey)}
        required={field.required && !stored}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        type={
          secret
            ? "password"
            : field.type === PlatformIntegrationFieldType.URL
              ? "url"
              : "text"
        }
        autoComplete={secret ? "new-password" : "off"}
        placeholder={
          secret && stored
            ? STORED_SECRET_MASK
            : (field.placeholder ?? undefined)
        }
        multiline={field.type === PlatformIntegrationFieldType.MULTILINE}
        minRows={
          field.type === PlatformIntegrationFieldType.MULTILINE ? 3 : undefined
        }
        inputProps={
          field.maxLength ? { maxLength: field.maxLength } : undefined
        }
        helperText={helper}
        errorText={error}
        disabled={disabled}
        fullWidth
      />
    </Stack>
  );
};

export default IntegrationFieldInput;
